import * as React from 'react';
import commonStyles from '../../assets/styles/common.scss';
import { useDispatch } from 'react-redux';
import { isDevWoWSClient } from '~/utils/devMode';
import equal from 'fast-deep-equal/react';
import { IPortProps } from '~/components/Port/AbstractPort';
import { closePort as closeClientPort, openPort } from '~/api/WoWsClient';
import { playButtonClickSound } from '~/api/WoWsClient';
import History from '~/utils/history';
import { urlsMap } from '~/utils/menu';
import { openBundleById, openBundleByUrl, openCategoryByName, openSalvageByExternalCode } from '~/utils/category';
import BundlePort from '~/components/Port/BundlePort/BundlePort';
import { PortPreviewType, type IPortContains, isArmoryPort } from '~/components/Port/settings';
import Account from '~/account/Account';
import PurchaseProcessor from '~/processors/PurchaseProcessor';
import { getChildBundle, getParentRandomBundleByChild, hasRestricInteraction, isFreeBundle } from '~/utils/bundles';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import store, { RootState, useAppSelector } from '~/Store';
import AuctionLotPort from '~/components/Port/AuctionLotPort/AuctionLotPort';
import { getLotByDisplayId, openLotById } from '~/utils/auction';
import ContainerPort from '~/components/Port/ContainerPort/ContainerPort';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS } from '~/const';
import DefaultPort from '~/components/Port/DefaultPort/DefaultPort';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';
import { appActions } from '~/Store/appSlice';

export const changeBackground = (isChange: boolean) => {
    const className = commonStyles.transparent;

    if (isDevWoWSClient()) {
        return;
    }

    if (isChange) {
        document.body.classList.add(className);
    } else {
        document.body.classList.remove(className);
    }
};

interface IPortContainer {
    match: Pick<IPort, 'shipId' | 'exteriorId' | 'id' | 'portPreviewType' | 'itemType' | 'sourceUrl'>;
}

const stateSelector = (state: RootState) => {
    return {
        focusPreset: state.app.focusPreset,
        currentPage: state.app.currentPage,
        port: state.app.port,
        bundles: state.app.bundles,
        purchasedLimitedBundles: state.account.purchasedLimitedBundles,
        deniedBundlesByUniqueItems: state.account.deniedBundlesByUniqueItems,
        selectedRandomBundles: state.account.selectedRandomBundles,
        auctions: state.auction.activeAuctions,
        inventory: state.account.inventory,
        balance: state.account.balance,
        coupons: state.account.coupons,
        lootboxes: state.lootbox.lootboxes,
        categories: state.app.categories,
    };
};

const PortContainer = (props: IPortContainer): any => {
    const dispatch = useDispatch();
    const state = useAppSelector(stateSelector, equal);
    const id = props.match?.id || state.port?.id;
    const shipId = state.port?.shipId || props.match?.shipId;
    const exteriorId = state.port?.exteriorId || props.match?.exteriorId;
    const itemType = state.port?.itemType || props.match?.itemType;
    const sourceUrl = state.port?.sourceUrl;
    const portPreviewType = state.port?.portPreviewType || props.match?.portPreviewType;
    const lootbox = state.lootboxes?.[state.port.additionalData?.lootboxId];
    const portContains: IPortContains = state.port.additionalData?.portContains || [];

    let bundle = state.bundles[id];
    let currentBundle = bundle;

    if (+id > 0) {
        if (bundle && bundle.isRandom) {
            bundle = Account.getRandomBundleChild(state.selectedRandomBundles, bundle);
        } else if (!bundle) {
            if (portPreviewType !== PortPreviewType.AUCTION_LOT) {
                const childBundle = getChildBundle(state.bundles, +id);
                const parentBundleId = getParentRandomBundleByChild(state.bundles, childBundle.id);
                currentBundle = state.bundles[parentBundleId];
                bundle = childBundle;
            }
        }
    }

    const onClosePort = (withoutHistoryUpdate = false) => {
        changeBackground(false);
        closeClientPort();
        dispatch(appActions.changeVisibilityPort({ isVisible: false, shipId, exteriorId }));
        if (!withoutHistoryUpdate) {
            if (bundle) {
                if (bundle.isRandom) {
                    return openBundleById(bundle.parentBundleId || bundle.id, state.focusPreset);
                }
                return openBundleByUrl(state.currentPage?.name, bundle.id, state.focusPreset);
            } else if (portPreviewType === PortPreviewType.AUCTION_LOT) {
                return openLotById(id.toString());
            } else if (portPreviewType === PortPreviewType.SALVAGE) {
                return openSalvageByExternalCode(String(id), state.port);
            }
            History.navigate(sourceUrl || urlsMap.home);
        }
    };

    const onPurchaseBundle = () => {
        const _isRandomBundle = bundle.isRandom;
        let parentBundleId: number = null;
        if (_isRandomBundle) {
            parentBundleId = getParentRandomBundleByChild(state.bundles, bundle.id);
        }

        const callback = () => {
            onClosePort(true);

            if (!bundle.serialPurchase || !bundle.nextBundle) {
                if (_isRandomBundle && parentBundleId) {
                    openBundleById(parentBundleId);
                } else {
                    openCategoryByName(state.currentPage?.name);
                }
            } else {
                const nextBundle = state.bundles[bundle.nextBundle];
                if (!nextBundle) {
                    openCategoryByName(state.currentPage?.name);
                } else {
                    openBundleByUrl(state.currentPage?.name, nextBundle.id);
                }
            }
        };

        const purchaseProcessor = new PurchaseProcessor(currentBundle);
        purchaseProcessor.purchaseBundleFromPopup(bundle, callback.bind(this), null, isFreeBundle(bundle));
    };

    React.useEffect(() => {
        // Macro queue
        setTimeout(() => {
            if (window?.tooltipProvider?.isVisible()) {
                window.tooltipProvider.hide();
            }
        });

        if (portPreviewType === PortPreviewType.CONTAINER && !lootbox) {
            onClosePort();
            return;
        }

        if (!state.port?.id && isArmoryPort(props.match.portPreviewType)) {
            openPort(props.match.shipId, props.match.exteriorId, props.match.itemType, props.match.portPreviewType, props.match.id, true, true, sourceUrl);
        }

        updateBrowserControlState(true);

        changeBackground(true);

        let options = null;

        if (portPreviewType === PortPreviewType.BUNDLE || portPreviewType === PortPreviewType.CONTAINER) {
            options = { bundle_id: id };
        } else if (portPreviewType === PortPreviewType.AUCTION_LOT) {
            options = { lot_id: id };
        }

        if (options) {
            dwhExport.send(DWH_EVENTS.OPEN_PORT, options);
        }
    }, []);

    useKeyDown((keyCode, event) => {
        if (KEYS_CODE.ESC === keyCode) {
            const app = store.getState().app;
            if (!!app.popupActive) {
                return;
            }
            playButtonClickSound();
            onClosePort();
        }
    }, []);

    React.useEffect(() => {
        if (!state.port?.id) {
            return;
        }

        const startTime = Date.now();

        return () => {
            const endTime = Date.now();
            const duration = Math.floor((endTime - startTime) / 1000);
            dwhExport.send(DWH_EVENTS.CLOSE_PORT, { duration });
        };
    }, []);

    const defaultProps: IPortProps = {
        onClose: onClosePort,
        shipId,
        portPreviewType,
        exteriorId,
        itemType,
        bundle: bundle,
    };

    switch (portPreviewType) {
        case PortPreviewType.BUNDLE: {
            let isDisabledPurchase = Account.isDisabledForPurchase(state.purchasedLimitedBundles, state.deniedBundlesByUniqueItems, bundle);

            if (!isDisabledPurchase && bundle.isRandom) {
                const parentBundleId = getParentRandomBundleByChild(state.bundles, bundle.id);
                isDisabledPurchase = state.selectedRandomBundles[parentBundleId] !== bundle.id;
            }

            if (!isDisabledPurchase) {
                isDisabledPurchase = hasRestricInteraction(bundle);
            }

            if (isDisabledPurchase) {
                return <DefaultPort bundle={bundle} inventory={state.inventory} {...defaultProps} />;
            }

            return (
                <BundlePort
                    bundle={bundle}
                    isDisabledPurchase={isDisabledPurchase}
                    onPurchaseBundle={onPurchaseBundle}
                    inventory={state.inventory}
                    balance={state.balance}
                    coupons={state.coupons}
                    {...defaultProps}
                />
            );
        }

        case PortPreviewType.AUCTION_LOT: {
            const lot = getLotByDisplayId(id.toString(), state.auctions);

            return <AuctionLotPort lot={lot} inventory={state.inventory} {...defaultProps} />;
        }

        case PortPreviewType.CONTAINER: {
            return <ContainerPort {...defaultProps} lootbox={lootbox} />;
        }

        case PortPreviewType.DEFAULT:
        default:
            return <DefaultPort bundle={bundle} portContains={portContains} inventory={state.inventory} {...defaultProps} />;
    }
};

export default PortContainer;
